import { deriveRoomId, derivePsk, generateToken } from "./crypto/keys";
import { toBase64Url } from "./crypto/util";
import type { Role } from "./crypto/handshake";
import { SignalingClient, type SignalMessage } from "./net/signaling";
import { Peer } from "./net/peer";
import {
  TransferSession,
  type Completed,
  type IncomingOffer,
} from "./transfer/session";
import { el, formatBytes, sanitizeFilename } from "./ui/dom";
import { toast } from "./ui/toast";
import { confirmIncoming } from "./ui/modal";
import { ProgressList } from "./ui/progress";
import { loadSettings, updateSetting } from "./ui/settings";
import { ICON } from "./ui/icons";
import { SITE_ORIGIN } from "./config";

// Minimal File System Access API types (not in the default DOM lib).
type WritableLike = {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
};
type FileHandleLike = { createWritable(): Promise<WritableLike> };
type DirHandleLike = {
  name: string;
  getFileHandle(n: string, o: { create: boolean }): Promise<FileHandleLike>;
};

/** Mount the interactive transfer view. `token` is null in create mode. */
export function mountApp(
  container: HTMLElement,
  token: Uint8Array | null
): void {
  new EnclaveApp(container, token).init();
}

class EnclaveApp {
  private readonly token: Uint8Array;
  private readonly isCreator: boolean;
  private role: Role = "responder";

  private psk?: Uint8Array;
  private signaling?: SignalingClient;
  private peer?: Peer;
  private session?: TransferSession;
  private ready = false;
  private staged: File[] = [];

  private verified = false;
  private receiveDir?: DirHandleLike;

  private readonly progress: ProgressList;
  private readonly transfers = el("div", { class: "transfers" });
  private readonly statusDot = el("span", { class: "dot wait" });
  private readonly statusText = el("span", { text: "Starting\u2026" });
  private readonly verifyPanel = el("div", {
    class: "verify",
    style: "display:none",
  });
  private readonly recvDest = el("span", {
    class: "recv-dest",
    text: "Downloads folder",
  });
  private readonly stagedBox = el("div", { class: "staged" });
  private linkSection = el("div");

  constructor(
    private readonly container: HTMLElement,
    token: Uint8Array | null
  ) {
    this.isCreator = token === null;
    this.token = token ?? generateToken();
    this.progress = new ProgressList(this.transfers);
  }

  init(): void {
    this.render();
    void this.connect();
  }

  // ---------- UI ----------

  private render(): void {
    const fileInput = el("input", {
      type: "file",
      multiple: true,
      hidden: true,
    }) as HTMLInputElement;
    fileInput.addEventListener("change", () => {
      if (fileInput.files) this.stageOrSend([...fileInput.files]);
      fileInput.value = "";
    });

    const dropzone = el(
      "div",
      {
        class: "dropzone",
        onclick: () => fileInput.click(),
        ondragover: (e: DragEvent) => {
          e.preventDefault();
          dropzone.classList.add("drag");
        },
        ondragleave: () => dropzone.classList.remove("drag"),
        ondrop: (e: DragEvent) => {
          e.preventDefault();
          dropzone.classList.remove("drag");
          if (e.dataTransfer?.files)
            this.stageOrSend([...e.dataTransfer.files]);
        },
      },
      el("div", { class: "dz-icon", html: ICON.upload }),
      el("h3", { text: "Drop files here" }),
      el(
        "p",
        {},
        "or ",
        el("span", { class: "browse", text: "browse" }),
        " to choose"
      )
    );

    const actions = el("div", { class: "actions-row" });
    // Folder selection only where supported (e.g. desktop Chrome/Edge), never on iOS Safari.
    const folderInput = el("input", {
      type: "file",
      hidden: true,
    }) as HTMLInputElement;
    if ("webkitdirectory" in folderInput) {
      folderInput.webkitdirectory = true;
      folderInput.multiple = true;
      folderInput.addEventListener("change", () => {
        if (folderInput.files) this.stageOrSend([...folderInput.files]);
        folderInput.value = "";
      });
      actions.append(
        el(
          "button",
          { class: "btn", onclick: () => folderInput.click() },
          el("span", { html: ICON.folder }),
          "Send a folder"
        ),
        folderInput
      );
    }

    const statusPill = el(
      "div",
      { class: "statuspill" },
      this.statusDot,
      this.statusText
    );

    const panel = el(
      "div",
      { class: "panel" },
      dropzone,
      actions,
      fileInput,
      this.stagedBox,
      this.linkSection,
      statusPill,
      this.verifyPanel,
      this.buildReceiveRow()
    );

    if (this.isCreator) {
      panel.append(
        el(
          "div",
          { class: "cancel-row" },
          el(
            "button",
            { class: "btn btn-ghost", onclick: () => this.cancel() },
            "Cancel share"
          )
        )
      );
    }

    this.container.append(panel, this.transfers, this.buildSettings());

    if (this.isCreator) this.renderLink();
  }

  private renderLink(): void {
    const origin = import.meta.env.DEV ? location.origin : SITE_ORIGIN;
    const link = `${origin}/#${toBase64Url(this.token)}`;
    const input = el("input", {
      value: link,
      readonly: true,
      "aria-label": "Share link",
    }) as HTMLInputElement;

    const copyBtn = el(
      "button",
      {
        class: "btn btn-primary",
        onclick: async () => {
          try {
            await navigator.clipboard.writeText(link);
            toast("Link copied", "success");
          } catch {
            input.select();
          }
        },
      },
      el("span", { html: ICON.copy }),
      "Copy"
    );

    const row = el("div", { class: "linkbox" }, input, copyBtn);
    if (typeof navigator.share === "function") {
      row.append(
        el(
          "button",
          {
            class: "btn",
            "aria-label": "Share",
            onclick: () =>
              navigator
                .share({ title: "Enclave", url: link })
                .catch(() => void 0),
          },
          el("span", { html: ICON.share })
        )
      );
    }

    const qr = el("div", { class: "qr" });
    const canvas = document.createElement("canvas");
    qr.append(canvas);
    void import("qrcode").then((QR) =>
      QR.toCanvas(canvas, link, { width: 180, margin: 1 }).catch(() =>
        qr.remove()
      )
    );

    const hint = el("p", {
      class: "muted",
      style: "font-size:12.5px;text-align:center;margin:10px 0 0",
      text: "Share over a channel you trust, or have them scan it in person. Works once.",
    });

    this.linkSection.replaceChildren(
      el(
        "div",
        { class: "share-head", style: "margin-top:16px" },
        el("h2", { text: "Share this link" })
      ),
      row,
      qr,
      hint
    );
  }

  private buildSettings(): HTMLElement {
    const settings = loadSettings();
    const autoCheckbox = el("input", { type: "checkbox" }) as HTMLInputElement;
    autoCheckbox.checked = settings.autoAccept;
    autoCheckbox.addEventListener("change", () =>
      updateSetting("autoAccept", autoCheckbox.checked)
    );

    return el(
      "div",
      { class: "settings" },
      el(
        "label",
        { class: "toggle" },
        autoCheckbox,
        el("span", { text: "Auto-accept incoming files" })
      )
    );
  }

  private buildReceiveRow(): HTMLElement {
    const row = el(
      "div",
      { class: "recv" },
      el("span", { class: "recv-ico", html: ICON.folder }),
      el("span", { class: "recv-label", text: "Received files go to" }),
      this.recvDest
    );
    const picker = (
      window as unknown as {
        showDirectoryPicker?: () => Promise<DirHandleLike>;
      }
    ).showDirectoryPicker;
    if (typeof picker === "function") {
      row.append(
        el(
          "button",
          { class: "btn", onclick: () => void this.chooseFolder(picker) },
          "Change"
        )
      );
    }
    return row;
  }

  private async chooseFolder(
    picker: () => Promise<DirHandleLike>
  ): Promise<void> {
    try {
      const dir = await picker();
      this.receiveDir = dir;
      this.recvDest.textContent = dir.name;
      toast(`Saving received files to \u201c${dir.name}\u201d`, "success");
    } catch {
      /* user cancelled the picker */
    }
  }

  private setStatus(
    text: string,
    kind: "wait" | "live" | "fail" = "wait"
  ): void {
    this.statusText.textContent = text;
    this.statusDot.className = `dot ${kind}`;
  }

  /** Optional, manual peer verification (out-of-band). The crypto handshake
   *  already blocks a server/network MITM; this only adds cover for an
   *  intercepted link, so it is offered, not required. */
  private showVerify(sas: string): void {
    const panel = this.verifyPanel;
    const render = (): void => {
      if (this.verified) {
        panel.className = "verify ok";
        panel.replaceChildren(
          el(
            "div",
            { class: "verify-badge" },
            el("span", { html: ICON.check }),
            el("span", { text: `Verified \u00b7 code ${sas}` })
          )
        );
        return;
      }
      panel.className = "verify";
      panel.replaceChildren(
        el(
          "div",
          { class: "verify-head" },
          el("span", { html: ICON.shield }),
          el("span", { text: "Verify it\u2019s really them" }),
          el("span", { class: "verify-opt", text: "optional" })
        ),
        el("div", { class: "verify-code", text: sas }),
        el("p", {
          class: "verify-text",
          text: "If you like, check this code matches on the other screen \u2014 a safeguard if the link was ever intercepted.",
        }),
        el(
          "div",
          { class: "verify-actions" },
          el(
            "button",
            {
              class: "btn btn-primary",
              onclick: () => {
                this.verified = true;
                render();
              },
            },
            el("span", { html: ICON.check }),
            "Codes match"
          ),
          el(
            "button",
            {
              class: "btn btn-ghost",
              onclick: () => {
                panel.style.display = "none";
              },
            },
            "Dismiss"
          )
        )
      );
    };
    render();
    panel.style.display = "";
  }

  // ---------- connection ----------

  private async connect(): Promise<void> {
    const roomId = await deriveRoomId(this.token);
    this.psk = await derivePsk(this.token);

    this.signaling = new SignalingClient({
      onMessage: (m) => void this.onSignal(m),
      onClose: () => void 0,
      onError: () => this.setStatus("Connection error", "fail"),
    });

    try {
      await this.signaling.connect(roomId);
      this.setStatus(
        this.isCreator
          ? "Waiting for the other person\u2026"
          : "Connecting\u2026"
      );
    } catch {
      this.setStatus("Could not reach the server", "fail");
    }
  }

  private async onSignal(msg: SignalMessage): Promise<void> {
    switch (msg.type) {
      case "welcome":
        this.role = msg.initiator ? "initiator" : "responder";
        this.createPeer();
        if (msg.peers === 2 && msg.initiator) await this.peer!.start();
        break;
      case "peers":
        if (msg.peers === 2 && this.role === "initiator")
          await this.peer?.start();
        break;
      case "peer-left":
        this.setStatus("The other person disconnected", "fail");
        break;
      case "full":
        this.setStatus("This link is already in use", "fail");
        break;
      case "offer":
      case "answer":
      case "ice":
        await this.peer?.handleSignal(msg);
        break;
    }
  }

  private createPeer(): void {
    if (this.peer) return;
    this.peer = new Peer(this.role === "initiator", {
      onSignal: (m) => this.signaling?.send(m),
      onChannelOpen: (ch) => void this.onChannelOpen(ch),
      onStateChange: (st) => {
        if (st === "connecting") this.setStatus("Connecting\u2026");
      },
      onFailed: () =>
        this.setStatus(
          "Couldn\u2019t establish a direct connection \u2014 your network may be too restrictive.",
          "fail"
        ),
    });
  }

  private async onChannelOpen(channel: RTCDataChannel): Promise<void> {
    this.setStatus("Securing channel\u2026");
    this.session = new TransferSession(channel, this.role, this.psk!, {
      onReady: (sas) => this.onReady(sas),
      onIncomingOffer: (offer, decide) => this.onIncomingOffer(offer, decide),
      onProgress: (p) => this.progress.update(p),
      onComplete: (c) => this.onComplete(c),
      onError: (m) => toast(m, "error"),
    });
    await this.session.begin();
  }

  private onReady(sas: string): void {
    this.ready = true;
    this.setStatus("Connected \u2014 end-to-end encrypted", "live");
    this.showVerify(sas);
    // Direct channel is up and authenticated; the relay is no longer needed.
    this.signaling?.close();
    if (this.staged.length) {
      const files = this.staged;
      this.staged = [];
      this.renderStaged();
      this.session!.sendFiles(files).catch((e) =>
        toast(String(e?.message ?? e), "error")
      );
    }
  }

  private onIncomingOffer(
    offer: IncomingOffer,
    decide: (accept: boolean) => void
  ): void {
    // Warn on iOS/Safari-style browsers that can't stream very large files to disk.
    if (!("showSaveFilePicker" in window) && offer.size > 512 * 1024 * 1024) {
      toast(
        "Large file: keep this tab open; it is held in memory until saved.",
        "warning",
        8000
      );
    }
    if (loadSettings().autoAccept) {
      decide(true);
      toast(`Receiving ${sanitizeFilename(offer.name)}\u2026`);
    } else {
      void confirmIncoming(offer.name, offer.size).then(decide);
    }
  }

  private onComplete(c: Completed): void {
    this.progress.complete(c);
    if (c.direction === "receive" && c.blob) {
      void this.saveFile(c.blob, c.name);
      toast(`Received ${sanitizeFilename(c.name)}`, "success");
    } else if (c.direction === "send") {
      toast(`Sent ${sanitizeFilename(c.name)}`, "success");
    }
  }

  private async saveFile(blob: Blob, name: string): Promise<void> {
    const safe = sanitizeFilename(name);
    if (this.receiveDir) {
      try {
        const handle = await this.receiveDir.getFileHandle(safe, {
          create: true,
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch {
        // permission denied or write failed — fall back to a normal download
      }
    }
    const url = URL.createObjectURL(blob);
    const a = el("a", { href: url, download: safe }) as HTMLAnchorElement;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private stageOrSend(files: File[]): void {
    if (!files.length) return;
    if (this.ready && this.session) {
      this.session
        .sendFiles(files)
        .catch((e) => toast(String(e?.message ?? e), "error"));
    } else {
      this.staged.push(...files);
      this.renderStaged();
      toast(
        `${files.length} file(s) ready \u2014 they\u2019ll send as soon as someone joins`
      );
    }
  }

  private renderStaged(): void {
    this.stagedBox.replaceChildren();
    if (!this.staged.length) return;
    this.stagedBox.append(
      el("div", {
        class: "staged-head",
        text: `Ready to send (${this.staged.length})`,
      })
    );
    this.staged.forEach((file, i) => {
      this.stagedBox.append(
        el(
          "div",
          { class: "staged-item" },
          el("span", { class: "staged-name", text: sanitizeFilename(file.name) }),
          el("span", { class: "staged-size muted", text: formatBytes(file.size) }),
          el(
            "button",
            {
              class: "staged-x",
              "aria-label": "Remove",
              onclick: () => this.removeStaged(i),
            },
            "\u00d7"
          )
        )
      );
    });
  }

  private removeStaged(index: number): void {
    this.staged.splice(index, 1);
    this.renderStaged();
  }

  /** Abort the whole share before connecting and return to the landing page. */
  private cancel(): void {
    this.ready = false;
    this.staged = [];
    try {
      this.session?.close();
    } catch {
      /* noop */
    }
    try {
      this.signaling?.close();
    } catch {
      /* noop */
    }
    try {
      this.peer?.close();
    } catch {
      /* noop */
    }
    location.hash = "/";
  }
}
